import { bulkCreateNominations } from '@api/nominations-api';
import { deleteNominee, type NomineeDetailResponse } from '@api/nominees-api';
import { getParticipants, type ParticipantResponse } from '@api/participants-api';
import Breadcrumbs from '@components/breadcrumbs';
import ConfirmationModal from '@components/confirmation-modal';
import Modal from '@components/modal';
import { useAuth } from '@hooks/use-auth';
import { useEdition } from '@hooks/use-edition';
import { useNominees } from '@hooks/use-nominees';
import { getAvatarColor, getInitials } from '@utils/avatar-utils';
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye,
  MapPin,
  Pencil,
  Search,
  Trash2,
  UserPlus,
  Vote,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { sileo } from 'sileo';

import EditNomineeModal from './edit-nominee-modal';
import NomineeDetailModal from './nominee-detail-modal';

function getPositionBadge(position: number): string {
  if (position === 1) return 'edition-nominee__pos--gold';
  if (position === 2) return 'edition-nominee__pos--silver';
  if (position === 3) return 'edition-nominee__pos--bronze';
  return 'edition-nominee__pos--default';
}

function getBarTier(position: number): string {
  if (position === 1) return 'nominee-votes-bar__fill--gold';
  if (position === 2) return 'nominee-votes-bar__fill--silver';
  if (position === 3) return 'nominee-votes-bar__fill--bronze';
  return '';
}

function getNomineeFullName(nominee: NomineeDetailResponse): string {
  return [nominee.participant.name, nominee.participant.surname].filter(Boolean).join(' ');
}

function getParticipantFullName(p: ParticipantResponse): string {
  return [p.name, p.surname].filter(Boolean).join(' ');
}

function getPageRange(currentPage: number, totalPages: number): number[] {
  const MAX_VISIBLE = 7;

  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: number[] = [1];

  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    start = 2;
    end = 5;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 4;
    end = totalPages - 1;
  }

  if (start > 2) pages.push(-1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push(-2);

  pages.push(totalPages);
  return pages;
}

export default function Nominees(): React.JSX.Element {
  const { isAdmin } = useAuth();
  const { editionId, categoryId } = useParams<{
    editionId: string;
    categoryId: string;
  }>();

  const { edition } = useEdition(editionId);
  const {
    nominees,
    editionCategoryId,
    categoryName: contextCategoryName,
    isLoading,
    refetch,
  } = useNominees(editionId, categoryId);
  const categoryName = contextCategoryName ?? nominees[0]?.category.name ?? 'Category';
  const editionLabel = edition ? `${edition.country} ${edition.year}`.trim() : 'Edition';

  const totalVotes = nominees.reduce((sum, n) => sum + n.voteCount, 0);
  const maxVotes = Math.max(...nominees.map((n) => n.voteCount), 1);
  const hasNoNominees = !isLoading && nominees.length === 0;

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [viewingNominee, setViewingNominee] = useState<NomineeDetailResponse | null>(null);
  const [editingNominee, setEditingNominee] = useState<NomineeDetailResponse | null>(null);
  const [deletingNominee, setDeletingNominee] = useState<NomineeDetailResponse | null>(null);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<Set<string>>(new Set());
  const [participantSearch, setParticipantSearch] = useState('');
  const [searchResults, setSearchResults] = useState<ParticipantResponse[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTotal, setSearchTotal] = useState(0);
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerTotalPages, setPickerTotalPages] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const DEBOUNCE_MS = 300;
  const PICKER_PAGE_SIZE = 50;

  const nominatedParticipantIds = useMemo(
    () => new Set(nominees.map((n) => n.participant.id)),
    [nominees],
  );

  // A nominee may only be nominated in an edition of its own site (ADR-012), so
  // the picker only ever offers participants of the edition's site. Without this
  // the list spans every site and the bulk create rejects the selection with a 409.
  const editionSiteId = edition?.siteId;

  const fetchPickerPage = useCallback(
    async (query: string, page: number, siteId: string): Promise<void> => {
      setIsSearching(true);
      try {
        const response = await getParticipants({
          search: query || undefined,
          siteId,
          page,
          pageSize: PICKER_PAGE_SIZE,
        });
        setSearchResults(response.data);
        setSearchTotal(response.meta.totalItems);
        setPickerTotalPages(response.meta.totalPages);
      } catch {
        setSearchResults([]);
        setSearchTotal(0);
        setPickerTotalPages(1);
      } finally {
        setIsSearching(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isPickerOpen) return;

    // The edition is still loading: hold the list rather than falling back to an
    // unscoped search, and let this effect run again once the site is known.
    if (!editionSiteId) {
      setIsSearching(true);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => {
        void fetchPickerPage(participantSearch, pickerPage, editionSiteId);
      },
      participantSearch ? DEBOUNCE_MS : 0,
    );

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [participantSearch, pickerPage, isPickerOpen, editionSiteId, fetchPickerPage]);

  const filteredParticipants = useMemo(
    () => searchResults.filter((p) => !nominatedParticipantIds.has(p.id)),
    [searchResults, nominatedParticipantIds],
  );

  const handlePickerSearchChange = (value: string): void => {
    setParticipantSearch(value);
    setPickerPage(1);
  };

  const toggleParticipant = (id: string): void => {
    setSelectedParticipantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const openPicker = (): void => {
    setSelectedParticipantIds(new Set());
    setParticipantSearch('');
    setSearchResults([]);
    setSearchTotal(0);
    setPickerPage(1);
    setPickerTotalPages(1);
    setIsPickerOpen(true);
  };

  const closePicker = (): void => {
    setIsPickerOpen(false);
    setSelectedParticipantIds(new Set());
    setParticipantSearch('');
    setSearchResults([]);
    setSearchTotal(0);
    setPickerPage(1);
    setPickerTotalPages(1);
  };

  const handleConfirmNominees = async (): Promise<void> => {
    if (!editionCategoryId || selectedParticipantIds.size === 0) return;

    try {
      await bulkCreateNominations({
        editionCategoryId,
        participantIds: [...selectedParticipantIds],
      });
      sileo.success({ title: 'Nominees added successfully' });
      closePicker();
      refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create nominations';
      sileo.error({ title: message });
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Editions', to: '/editions' },
          {
            label: editionLabel,
            to: `/editions/${editionId}/categories`,
          },
          { label: categoryName },
        ]}
      />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">Nominees</h1>
          <p className="page-header__subtitle">
            {categoryName} &mdash; {nominees.length} nominees
          </p>
        </div>
        {isAdmin && (
          <div className="page-header__actions">
            <button type="button" className="btn btn-primary" onClick={openPicker}>
              <UserPlus size={16} />
              Add nominees
            </button>
          </div>
        )}
      </div>

      <div className="nav-links">
        <Link to={`/editions/${editionId}/categories`} className="nav-link">
          <ArrowLeft size={16} />
          Categories
        </Link>
        <Link to={`/editions/${editionId}/categories/${categoryId}/votes`} className="nav-link">
          <Vote size={16} />
          View votes
        </Link>
      </div>

      {!hasNoNominees && (
        <div className="stats-bar">
          <div className="stats-bar__item">
            <span className="stats-bar__label">Nominees</span>
            <span className="stats-bar__value">{nominees.length}</span>
          </div>
          <div className="stats-bar__item">
            <span className="stats-bar__label">Total votes</span>
            <span className="stats-bar__value">{totalVotes}</span>
          </div>
        </div>
      )}

      {hasNoNominees ? (
        <div className="participant-picker">
          <div className="participant-picker__header">
            <div className="participant-picker__title">
              <UserPlus size={20} />
              <div>
                <h3>No nominees yet</h3>
                <p className="participant-picker__subtitle">
                  There are no nominees for {categoryName}. Add participants to nominate them.
                </p>
              </div>
            </div>
          </div>
          {isAdmin && (
            <div className="participant-picker__footer">
              <button type="button" className="btn btn-primary" onClick={openPicker}>
                <UserPlus size={16} />
                Add nominees
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="edition-nominees-grid">
          {nominees.map((nominee, index) => {
            const fullName = getNomineeFullName(nominee);
            const position = nominee.ranking ?? index + 1;

            return (
              <div className="edition-nominee-card" key={nominee.id}>
                <div className="edition-nominee-card__header">
                  {nominee.participant.photoUrl ? (
                    <img
                      src={nominee.participant.photoUrl}
                      alt={fullName}
                      className="edition-nominee-card__avatar"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div
                      className="edition-nominee-card__avatar"
                      style={{ backgroundColor: getAvatarColor(fullName) }}
                    >
                      {getInitials(fullName)}
                    </div>
                  )}
                  <div className="edition-nominee-card__info">
                    <h3 className="edition-nominee-card__name">{fullName}</h3>
                    <div className="edition-nominee-card__meta">
                      {nominee.participant.city && (
                        <span className="edition-nominee-card__city">
                          <MapPin size={13} />
                          {nominee.participant.city}
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`edition-nominee__pos edition-nominee__pos--lg ${getPositionBadge(position)}`}
                  >
                    #{position}
                  </span>
                </div>

                <div className="nominee-votes-bar">
                  <div className="nominee-votes-bar__header">
                    <span className="nominee-votes-bar__count">{nominee.voteCount}</span>
                    <span className="nominee-votes-bar__label">votes</span>
                    <span className="nominee-votes-bar__pct">
                      {totalVotes > 0 ? Math.round((nominee.voteCount / totalVotes) * 100) : 0}%
                    </span>
                  </div>
                  <div className="nominee-votes-bar__track">
                    <div
                      className={`nominee-votes-bar__fill ${getBarTier(position)}`}
                      style={{ width: `${(nominee.voteCount / maxVotes) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="edition-nominee-card__footer">
                  {nominee.participant.profileUrl && (
                    <a
                      href={nominee.participant.profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-ghost btn-sm"
                    >
                      <ExternalLink size={14} />
                      Profile
                    </a>
                  )}
                  <div className="edition-nominee-card__actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-icon btn-sm"
                      data-tooltip="View details"
                      aria-label="View details"
                      onClick={() => setViewingNominee(nominee)}
                    >
                      <Eye size={16} />
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          data-tooltip="Edit nominee"
                          aria-label="Edit nominee"
                          onClick={() => setEditingNominee(nominee)}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          data-tooltip="Delete nominee"
                          aria-label="Delete nominee"
                          onClick={() => setDeletingNominee(nominee)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={isPickerOpen} onClose={closePicker} title={`Add nominees — ${categoryName}`}>
        <div className="participant-picker participant-picker--modal">
          <div className="participant-picker__toolbar">
            <div className="participant-picker__search-wrapper">
              <Search size={16} className="participant-picker__search-icon" />
              <input
                type="text"
                className="participant-picker__search"
                placeholder="Search by name or city..."
                value={participantSearch}
                onChange={(e) => handlePickerSearchChange(e.target.value)}
              />
            </div>
            <span className="participant-picker__count">
              {selectedParticipantIds.size} selected &middot; {searchTotal} total
            </span>
          </div>

          <div className="participant-picker__list">
            {isSearching ? (
              <div className="participant-picker__empty">Searching...</div>
            ) : filteredParticipants.length === 0 ? (
              <div className="participant-picker__empty">
                No participants found in this edition&rsquo;s site
              </div>
            ) : (
              filteredParticipants.map((p) => {
                const fullName = getParticipantFullName(p);
                const isSelected = selectedParticipantIds.has(p.id);

                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`participant-picker__item${isSelected ? ' participant-picker__item--selected' : ''}`}
                    onClick={() => toggleParticipant(p.id)}
                  >
                    <div
                      className="participant-picker__avatar"
                      style={{ backgroundColor: getAvatarColor(fullName) }}
                    >
                      {isSelected ? <Check size={14} /> : getInitials(fullName)}
                    </div>
                    <div className="participant-picker__info">
                      <span className="participant-picker__name">{fullName}</span>
                      {p.city && (
                        <span className="participant-picker__city">
                          <MapPin size={11} />
                          {p.city}
                        </span>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      className="participant-picker__checkbox"
                      checked={isSelected}
                      readOnly
                      tabIndex={-1}
                    />
                  </button>
                );
              })
            )}
          </div>

          {pickerTotalPages > 1 && (
            <div className="pagination">
              <span className="pagination__info">
                Page {pickerPage} of {pickerTotalPages} &middot; {searchTotal} participants
              </span>

              <div className="pagination__controls">
                <button
                  type="button"
                  className="pagination__btn"
                  disabled={pickerPage === 1}
                  aria-label="Previous page"
                  onClick={() => setPickerPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>

                {getPageRange(pickerPage, pickerTotalPages).map((page) =>
                  page < 0 ? (
                    <span key={page} className="pagination__ellipsis">
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={`pagination__btn${page === pickerPage ? ' pagination__btn--active' : ''}`}
                      onClick={() => setPickerPage(page)}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="pagination__btn"
                  disabled={pickerPage === pickerTotalPages}
                  aria-label="Next page"
                  onClick={() => setPickerPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          <div className="participant-picker__footer">
            <span className="participant-picker__footer-count">
              {selectedParticipantIds.size}{' '}
              {selectedParticipantIds.size === 1 ? 'participant' : 'participants'} selected
            </span>
            <button
              type="button"
              className="btn btn-primary"
              disabled={selectedParticipantIds.size === 0}
              onClick={() => void handleConfirmNominees()}
            >
              <UserPlus size={16} />
              Confirm nominees
            </button>
          </div>
        </div>
      </Modal>

      {viewingNominee && (
        <NomineeDetailModal
          isOpen
          onClose={() => setViewingNominee(null)}
          nominee={viewingNominee}
          editionId={editionId ?? ''}
        />
      )}

      <EditNomineeModal
        isOpen={editingNominee !== null}
        onClose={() => {
          setEditingNominee(null);
          refetch();
        }}
        nominee={editingNominee}
      />

      <ConfirmationModal
        isOpen={deletingNominee !== null}
        onClose={() => setDeletingNominee(null)}
        onConfirm={async () => {
          if (deletingNominee) {
            await deleteNominee(deletingNominee.id);
            sileo.success({ title: 'Nominee deleted successfully' });
            setDeletingNominee(null);
            refetch();
          }
        }}
        title="Delete nominee"
        message={`Are you sure you want to delete "${deletingNominee ? getNomineeFullName(deletingNominee) : ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </>
  );
}
